import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Mail, AlertTriangle } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ResendEmail {
  id: string
  to: string[] | string
  from: string
  subject: string
  created_at: string
  last_event?: string
}

interface ResendListResponse {
  configured: boolean
  data?: ResendEmail[]
  error?: string
}

interface ResendDetail {
  id: string
  to: string[] | string
  from: string
  subject: string
  html?: string
  text?: string
  created_at: string
  last_event?: string
}

async function fetchEmails(): Promise<ResendListResponse> {
  const { data, error } = await supabase.functions.invoke('admin-emails', {
    body: { action: 'list', limit: 100 },
  })
  if (error) throw new Error(String(error))
  if ((data as { error?: string; configured?: boolean })?.error && (data as { configured?: boolean }).configured !== false) {
    throw new Error((data as { error: string }).error)
  }
  return data as ResendListResponse
}

async function fetchEmail(id: string): Promise<ResendDetail> {
  const { data, error } = await supabase.functions.invoke('admin-emails', {
    body: { action: 'get', id },
  })
  if (error) throw new Error(String(error))
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error)
  return (data as { email: ResendDetail }).email
}

function eventColor(event?: string): string {
  if (!event) return ''
  if (event === 'delivered') return 'border-green-300 bg-green-50 text-green-800'
  if (event === 'opened' || event === 'clicked') return 'border-blue-300 bg-blue-50 text-blue-800'
  if (event === 'bounced' || event === 'complained' || event === 'failed')
    return 'border-red-300 bg-red-50 text-red-800'
  return ''
}

export function EmailLogPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-emails'],
    queryFn: fetchEmails,
    staleTime: 30 * 1000,
  })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data: detail } = useQuery({
    queryKey: ['admin-emails', selectedId],
    queryFn: () => fetchEmail(selectedId!),
    enabled: !!selectedId,
  })

  if (data && !data.configured) {
    return (
      <div>
        <PageHeader title="Email Log" description="Resend delivery log" />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8" />
            <p>RESEND_API_KEY is not configured on the admin-emails function.</p>
            <p className="mt-1 text-xs">
              Deploy the function with the secret to surface email history here.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const emails = data?.data ?? []

  return (
    <div>
      <PageHeader
        title="Email Log"
        description={`Last ${emails.length} emails sent via Resend`}
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8" />
            <p>{error instanceof Error ? error.message : 'Failed to load emails'}</p>
          </CardContent>
        </Card>
      ) : emails.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Mail className="mb-4 h-12 w-12" />
          <p>No emails found.</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                  <th className="px-4 py-2 text-left">Sent</th>
                  <th className="px-4 py-2 text-left">To</th>
                  <th className="px-4 py-2 text-left">From</th>
                  <th className="px-4 py-2 text-left">Subject</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((e) => (
                  <tr
                    key={e.id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/20"
                    onClick={() => setSelectedId(e.id)}
                  >
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {format(new Date(e.created_at), 'MMM d HH:mm')}
                    </td>
                    <td className="px-4 py-2">{Array.isArray(e.to) ? e.to.join(', ') : e.to}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{e.from}</td>
                    <td className="px-4 py-2 max-w-md truncate">{e.subject}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={eventColor(e.last_event)}>
                        {e.last_event ?? 'sent'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <DialogContent className="max-h-[80vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="truncate">{detail?.subject ?? 'Email'}</DialogTitle>
          </DialogHeader>
          {!detail ? (
            <Skeleton className="h-64" />
          ) : (
            <div className="space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">From:</span> {detail.from}</div>
                <div><span className="text-muted-foreground">To:</span> {Array.isArray(detail.to) ? detail.to.join(', ') : detail.to}</div>
                <div><span className="text-muted-foreground">Sent:</span> {format(new Date(detail.created_at), 'PPpp')}</div>
                <div><span className="text-muted-foreground">Status:</span> {detail.last_event ?? 'sent'}</div>
              </div>
              {detail.html && (
                <div className="rounded border bg-muted/20">
                  <iframe
                    title={`email-${detail.id}`}
                    srcDoc={detail.html}
                    sandbox=""
                    className="h-[50vh] w-full rounded"
                  />
                </div>
              )}
              {!detail.html && detail.text && (
                <pre className="max-h-[50vh] overflow-auto rounded bg-muted p-3 text-xs">{detail.text}</pre>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSelectedId(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
